import { LINKS } from '@/constants/links';
import { CategoryTiles } from '@/components/tiles';
import { Category } from './functions/utils';
import { useState } from 'react';
import { Button } from '@/components/buttons';
import { useTranslation } from 'react-i18next';
import IconDown from '@/assets/icons/heroIcons/Chevron-down.svg';
import { mergeClasses } from '@/utils/style';

interface Props {
	categorySlug?: Category['slug'];
	subcategories: Category['subcategories'];
	variant?: 'default' | 'collapsible' | 'minimal';
	className?: string;
}

export function Categories(props: Props) {
	const isCollapsibleVariant = props.variant === 'collapsible' || props.variant === 'minimal';
	const showCollapseButton = isCollapsibleVariant && props.subcategories.length > 6;
	const { t } = useTranslation('gcss');

	const [collapsed, setCollapsed] = useState(
		isCollapsibleVariant && props.subcategories.length > 6
	);

	return (
		<section className={mergeClasses('mb-12', props.className)}>
			<div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:mt-12">
				{props.subcategories
					.slice(0, collapsed ? 6 : props.subcategories.length)
					.map((subCategory) => {
						const link = props.categorySlug
							? Array.isArray(subCategory.slug)
								? LINKS().gcssSubCategory(subCategory.slug)
								: LINKS().gcssSubCategory([props.categorySlug, subCategory.slug])
							: LINKS().gcssCategory(subCategory.slug[0]);

						return (
							<CategoryTiles
								analyticsParam={`discountCategory_${subCategory.slug}`}
								testId={`discountCategory_${subCategory.slug}`}
								to={link}
								key={Array.isArray(subCategory.slug) ? subCategory.slug.join('') : subCategory.slug}
								title={subCategory.title}
								image={
									subCategory.image
										? {
												src: subCategory.image,
												alt: subCategory.title,
											}
										: undefined
								}
							/>
						);
					})}
			</div>

			{isCollapsibleVariant && showCollapseButton && (
				<Button
					analyticsParam="showAllCategoriesButton"
					testId="showAllCategoriesButton"
					onClick={() => setCollapsed((prev) => !prev)}
					variant={props.variant === 'minimal' ? 'unstyled' : 'secondary'}
					className={
						props.variant === 'minimal'
							? 'mt-4 flex items-center font-semibold text-blue-500 hover:text-blue-700 focus:text-blue-700 focus:outline-hidden'
							: 'mx-auto mt-6 md:mt-12'
					}
					rightIcon={
						<IconDown
							className={mergeClasses(
								'h-4 w-4 transform transition-transform',
								collapsed ? 'rotate-0' : 'rotate-180'
							)}
						/>
					}
				>
					{collapsed ? t('category.showMore') : t('category.showLess')}
				</Button>
			)}
		</section>
	);
}
